/**
 * Sitemap Page - Sitemap Submission Status
 * 
 * V228: Enhanced with content breakdown display (web, image, video, news).
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Fade,
  Button,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Web as WebIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Article as NewsIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Link as LinkIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSitemapStatus } from '@/hooks/customer';
import { formatCompactNumber } from '@/services/customer/formatters';
import { ENGINE_COLORS_LOWER, SEMANTIC_COLORS } from '@/services/customer/constants';
import { MetricCard, InlineError, MetricCardSkeleton, TableSkeleton } from '@/components/customer';
import type { SitemapEntry, SitemapContentEntry, UrlInventoryEntry } from '@/types/customer';

type EngineFilter = 'google' | 'bing';
type StatusFilter = 'all' | 'success' | 'pending' | 'error';

const statusFilterOptions: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'success', label: 'Success' },
  { value: 'pending', label: 'Pending' },
  { value: 'error', label: 'Errors' },
];

// Content type icons and colors
const contentTypeConfig: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
  web: { icon: <WebIcon fontSize="small" />, label: 'Web Pages', color: '#1976d2' },
  image: { icon: <ImageIcon fontSize="small" />, label: 'Images', color: '#9c27b0' },
  video: { icon: <VideoIcon fontSize="small" />, label: 'Videos', color: '#ed6c02' },
  news: { icon: <NewsIcon fontSize="small" />, label: 'News', color: '#2e7d32' },
};

// URL Inventory Panel component
function UrlInventoryPanel({ 
  urls, 
  snapshotDate, 
  totalUrls 
}: { 
  urls: UrlInventoryEntry[]; 
  snapshotDate?: string; 
  totalUrls: number;
}) {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LinkIcon color="primary" />
              <Typography variant="h6">Sitemap URLs</Typography>
            </Stack>
            <Chip 
              label={`${totalUrls} URLs`} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
            {snapshotDate && (
              <Tooltip title="Last scanned date">
                <Chip
                  icon={<CalendarIcon sx={{ fontSize: 14 }} />}
                  label={new Date(snapshotDate).toLocaleDateString()}
                  size="small"
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              </Tooltip>
            )}
          </Box>
          <IconButton size="small">
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </Box>
        
        <Collapse in={expanded} timeout="auto">
          <Box sx={{ mt: 2 }}>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>URL</TableCell>
                    <TableCell align="center" width={100}>In Sitemap</TableCell>
                    <TableCell align="center" width={100}>Indexed</TableCell>
                    <TableCell align="center" width={80}>HTTP</TableCell>
                    <TableCell width={100}>First Seen</TableCell>
                    <TableCell width={100}>Last Seen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {urls.map((url) => (
                    <TableRow key={url.id} hover>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          noWrap 
                          sx={{ 
                            maxWidth: 400,
                            '&:hover': { textDecoration: 'underline', cursor: 'pointer' }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(url.url, '_blank');
                          }}
                        >
                          {url.url}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {url.inSitemap ? (
                          <SuccessIcon sx={{ fontSize: 18, color: SEMANTIC_COLORS.success }} />
                        ) : (
                          <ErrorIcon sx={{ fontSize: 18, color: SEMANTIC_COLORS.error }} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {url.isIndexed === true ? (
                          <SuccessIcon sx={{ fontSize: 18, color: SEMANTIC_COLORS.success }} />
                        ) : url.isIndexed === false ? (
                          <ErrorIcon sx={{ fontSize: 18, color: SEMANTIC_COLORS.error }} />
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {url.httpStatus ? (
                          <Chip 
                            label={url.httpStatus} 
                            size="small" 
                            sx={{ 
                              fontSize: 11,
                              height: 20,
                              bgcolor: url.httpStatus === 200 ? SEMANTIC_COLORS.success : SEMANTIC_COLORS.error,
                              color: 'white',
                            }} 
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {url.firstSeenAt ? new Date(url.firstSeenAt).toLocaleDateString() : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {url.lastSeenAt ? new Date(url.lastSeenAt).toLocaleDateString() : '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

// Content type card for breakdown display
function ContentCard({ content }: { content: SitemapContentEntry }) {
  const config = contentTypeConfig[content.type.toLowerCase()] || {
    icon: <WebIcon fontSize="small" />,
    label: content.type,
    color: '#757575',
  };
  const indexRate = content.submitted > 0 ? (content.indexed / content.submitted) * 100 : 0;

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Box sx={{ color: config.color }}>{config.icon}</Box>
          <Typography variant="subtitle2" sx={{ color: config.color }}>
            {config.label}
          </Typography>
        </Stack>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Submitted
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {formatCompactNumber(content.submitted)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Indexed
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {formatCompactNumber(content.indexed)}
            </Typography>
          </Grid>
        </Grid>
        <Box sx={{ mt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={indexRate}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': { bgcolor: config.color },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {indexRate.toFixed(0)}% indexed
          </Typography>
        </Box>
        {(content.errors > 0 || content.warnings > 0) && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {content.errors > 0 && (
              <Tooltip title="Errors">
                <Chip
                  icon={<ErrorIcon sx={{ fontSize: 12 }} />}
                  label={content.errors}
                  size="small"
                  sx={{ bgcolor: SEMANTIC_COLORS.error, color: 'white', height: 20, fontSize: 11 }}
                />
              </Tooltip>
            )}
            {content.warnings > 0 && (
              <Tooltip title="Warnings">
                <Chip
                  icon={<WarningIcon sx={{ fontSize: 12 }} />}
                  label={content.warnings}
                  size="small"
                  sx={{ bgcolor: SEMANTIC_COLORS.warning, color: 'white', height: 20, fontSize: 11 }}
                />
              </Tooltip>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

// Expandable row component for sitemap with content breakdown
function SitemapRow({ sitemap }: { sitemap: SitemapEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasContents = sitemap.contents && sitemap.contents.length > 0;

  const getStatusChip = (status: string) => {
    const config: Record<string, { color: string; icon: React.ReactNode }> = {
      SUCCESS: { color: SEMANTIC_COLORS.success, icon: <SuccessIcon sx={{ fontSize: 14 }} /> },
      PENDING: { color: SEMANTIC_COLORS.warning, icon: <PendingIcon sx={{ fontSize: 14 }} /> },
      ERROR: { color: SEMANTIC_COLORS.error, icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
      FAILED: { color: SEMANTIC_COLORS.error, icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
    };
    const { color, icon } = config[status] || { color: SEMANTIC_COLORS.info, icon: null };
    return (
      <Chip
        icon={icon as React.ReactElement}
        label={status}
        size="small"
        sx={{ bgcolor: color, color: 'white', fontWeight: 500 }}
      />
    );
  };

  // Calculate totals from contents or fallback to legacy fields
  const totals = useMemo(() => {
    if (!sitemap.contents || sitemap.contents.length === 0) {
      return {
        submitted: sitemap.urlsSubmitted ?? sitemap.urlsDiscovered ?? 0,
        indexed: sitemap.urlsIndexed ?? 0,
      };
    }
    return sitemap.contents.reduce(
      (acc, c) => ({
        submitted: acc.submitted + (c.submitted ?? 0),
        indexed: acc.indexed + (c.indexed ?? 0),
      }),
      { submitted: 0, indexed: 0 }
    );
  }, [sitemap]);

  const indexRate = totals.submitted > 0 ? (totals.indexed / totals.submitted) * 100 : 0;

  return (
    <>
      <TableRow
        sx={{
          '&:hover': { bgcolor: 'action.hover' },
          cursor: hasContents ? 'pointer' : 'default',
        }}
        onClick={() => hasContents && setExpanded(!expanded)}
      >
        <TableCell>
          {hasContents && (
            <IconButton size="small">
              {expanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Box>
            <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
              {sitemap.sitemapUrl}
            </Typography>
            {sitemap.isSitemapIndex && (
              <Chip label="Index" size="small" variant="outlined" sx={{ mt: 0.5, fontSize: 10 }} />
            )}
          </Box>
        </TableCell>
        <TableCell>{getStatusChip(sitemap.submissionStatus)}</TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={500}>
            {formatCompactNumber(totals.submitted)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={500}>
            {formatCompactNumber(totals.indexed)}
          </Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinearProgress
              variant="determinate"
              value={indexRate}
              sx={{
                width: 80,
                height: 6,
                borderRadius: 3,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  bgcolor: indexRate > 50 ? SEMANTIC_COLORS.success : SEMANTIC_COLORS.warning,
                },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {indexRate.toFixed(0)}%
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {sitemap.submittedAt
              ? new Date(sitemap.submittedAt).toLocaleDateString()
              : '-'}
          </Typography>
        </TableCell>
      </TableRow>

      {/* Expanded content breakdown */}
      {hasContents && (
        <TableRow>
          <TableCell colSpan={7} sx={{ py: 0, bgcolor: 'grey.50' }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ py: 2, px: 4 }}>
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                  Content Breakdown
                </Typography>
                <Grid container spacing={2}>
                  {sitemap.contents!.map((content) => (
                    <Grid item xs={6} md={3} key={content.type}>
                      <ContentCard content={content} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function CustomerSitemapPage() {
  const navigate = useNavigate();
  const [engine, setEngine] = useState<EngineFilter>('google');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const { data, isLoading, error, refetch } = useSitemapStatus({
    tool: engine === 'google' ? 'GSC' : 'BING',
  });

  const filteredSitemaps = useMemo(() => {
    const sitemaps = data?.data?.sitemaps ?? [];
    if (statusFilter === 'all') return sitemaps;

    return sitemaps.filter((sitemap) => {
      switch (statusFilter) {
        case 'success':
          return sitemap.submissionStatus === 'SUCCESS';
        case 'pending':
          return sitemap.submissionStatus === 'PENDING';
        case 'error':
          return sitemap.submissionStatus === 'ERROR' || sitemap.submissionStatus === 'FAILED';
        default:
          return true;
      }
    });
  }, [data?.data?.sitemaps, statusFilter]);

  // Calculate aggregate content stats across all sitemaps
  const contentStats = useMemo(() => {
    const sitemaps = data?.data?.sitemaps ?? [];
    const stats: Record<string, { submitted: number; indexed: number }> = {};

    sitemaps.forEach((sitemap) => {
      sitemap.contents?.forEach((content) => {
        const type = content.type.toLowerCase();
        if (!stats[type]) {
          stats[type] = { submitted: 0, indexed: 0 };
        }
        stats[type].submitted += content.submitted ?? 0;
        stats[type].indexed += content.indexed ?? 0;
      });
    });

    return stats;
  }, [data?.data?.sitemaps]);

  if (isLoading) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="text" width={350} height={24} />
        </Box>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} md={3} key={i}>
              <MetricCardSkeleton />
            </Grid>
          ))}
        </Grid>
        <TableSkeleton />
      </Box>
    );
  }

  const summary = data?.data?.summary;

  return (
    <Fade in timeout={300}>
      <Box sx={{ 
        maxWidth: { xs: 400, sm: 768, md: 900, lg: 1200 },
        mx: 'auto',
        px: { xs: 2, sm: 3, md: 4 },
        py: 3
      }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WebIcon />
                Sitemap Status
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sitemap submission status with content type breakdown
              </Typography>
            </Box>
            
            {/* Snapshot Info Display */}
            {data?.data?.snapshotDate && (
              <Box sx={{ minWidth: 200, textAlign: 'right' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  justifyContent: 'flex-end',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Data Snapshot
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {new Date(data.data.snapshotDate).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                      {data.data.snapshotDate === new Date().toISOString().split('T')[0] && (
                        <Chip label="Latest" size="small" color="primary" sx={{ ml: 1, height: 18 }} />
                      )}
                    </Typography>
                  </Box>
                </Box>
                {data?.data?.urlInventory && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {data.data.urlInventory.totalUrls} URLs in inventory
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {error && (
          <InlineError error={error} title="Failed to load sitemap data" onRetry={refetch} />
        )}

        {/* Engine Selector */}
        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={engine}
            exclusive
            onChange={(_, newEngine) => newEngine && setEngine(newEngine)}
            aria-label="search engine"
          >
            <ToggleButton
              value="google"
              sx={{
                '&.Mui-selected': {
                  backgroundColor: ENGINE_COLORS_LOWER.google,
                  color: 'white',
                  '&:hover': { backgroundColor: ENGINE_COLORS_LOWER.google },
                },
              }}
            >
              Google
            </ToggleButton>
            <ToggleButton
              value="bing"
              sx={{
                '&.Mui-selected': {
                  backgroundColor: ENGINE_COLORS_LOWER.bing,
                  color: 'white',
                  '&:hover': { backgroundColor: ENGINE_COLORS_LOWER.bing },
                },
              }}
            >
              Bing
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WebIcon color="primary" />
                  <Typography variant="body2" color="text.secondary">Total Sitemaps</Typography>
                </Box>
                <Typography variant="h4">{formatCompactNumber(summary?.totalSitemaps ?? 0)}</Typography>
                {data?.data?.snapshotDate && (
                  <Typography variant="caption" color="text.secondary">
                    Snapshot: {new Date(data.data.snapshotDate).toLocaleDateString()}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SuccessIcon color="success" />
                  <Typography variant="body2" color="text.secondary">Successful</Typography>
                </Box>
                <Typography variant="h4" sx={{ color: SEMANTIC_COLORS.success }}>
                  {formatCompactNumber(summary?.success ?? 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Successfully submitted
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PendingIcon color="warning" />
                  <Typography variant="body2" color="text.secondary">Pending</Typography>
                </Box>
                <Typography variant="h4" sx={{ color: SEMANTIC_COLORS.warning }}>
                  {formatCompactNumber(summary?.pending ?? 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Awaiting processing
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ErrorIcon color="error" />
                  <Typography variant="body2" color="text.secondary">Errors</Typography>
                </Box>
                <Typography variant="h4" sx={{ color: SEMANTIC_COLORS.error }}>
                  {formatCompactNumber(summary?.errors ?? 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Need attention
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Content Type Summary Cards (V228) */}
        {Object.keys(contentStats).length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Content Type Summary
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(contentStats).map(([type, stats]) => {
                const config = contentTypeConfig[type] || {
                  icon: <WebIcon />,
                  label: type,
                  color: '#757575',
                };
                const indexRate = stats.submitted > 0 ? (stats.indexed / stats.submitted) * 100 : 0;
                return (
                  <Grid item xs={6} md={3} key={type}>
                    <Card>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <Box sx={{ color: config.color }}>{config.icon}</Box>
                          <Typography variant="subtitle2">{config.label}</Typography>
                        </Stack>
                        <Typography variant="h5" sx={{ color: config.color }}>
                          {formatCompactNumber(stats.submitted)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCompactNumber(stats.indexed)} indexed ({indexRate.toFixed(0)}%)
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={indexRate}
                          sx={{
                            mt: 1,
                            height: 4,
                            borderRadius: 2,
                            '& .MuiLinearProgress-bar': { bgcolor: config.color },
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* URL Inventory Panel */}
        {data?.data?.urlInventory && data.data.urlInventory.urls.length > 0 && (
          <UrlInventoryPanel
            urls={data.data.urlInventory.urls}
            snapshotDate={data.data.urlInventory.snapshotDate}
            totalUrls={data.data.urlInventory.totalUrls}
          />
        )}

        {/* Filter Controls */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle2">Filter by status:</Typography>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              {statusFilterOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredSitemaps.length} of {data?.data?.sitemaps?.length ?? 0} sitemaps
          </Typography>
        </Box>

        {/* Sitemap Table with Expandable Rows */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Sitemap Details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click on a row to expand content breakdown
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell width={40} />
                    <TableCell>Sitemap URL</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Submitted</TableCell>
                    <TableCell align="right">Indexed</TableCell>
                    <TableCell>Index Rate</TableCell>
                    <TableCell>Last Submitted</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSitemaps.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No sitemaps found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSitemaps.map((sitemap, index) => (
                      <SitemapRow key={sitemap.sitemapUrl || index} sitemap={sitemap} />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
}

export default CustomerSitemapPage;
